<template>
  <v-container>
    <v-card>
      <v-card-title>
        Registered Users
        <v-spacer></v-spacer>
        <v-btn color="primary" prepend-icon="mdi-account-plus" :to="{ name: 'user-add' }">
          Add User
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        class="elevation-1"
      >
        <template v-slot:progress>
          <v-progress-linear color="primary" indeterminate></v-progress-linear>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            icon="mdi-delete"
            color="error"
            size="small"
            variant="text"
            @click="confirmDelete(item)"
          >
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete user {{ selectedUser?.username }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="tonal"
            @click="deleteUser"
            :loading="deleteLoading"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  createdAt: string; // LocalDateTime maps to string in TypeScript
  updatedAt: string; // LocalDateTime maps to string in TypeScript
  role: string;
}

const users = ref<User[]>([]);
const loading = ref(true);

const deleteDialog = ref(false);
const deleteLoading = ref(false);
const selectedUser = ref<User | null>(null);

const headers = [
  { title: 'ID', align: 'start', key: 'id' },
  { title: 'Username', key: 'username' },
  { title: 'First Name', key: 'firstName' },
  { title: 'Last Name', key: 'lastName' },
  { title: 'Created At', key: 'createdAt' },
  { title: 'Updated At', key: 'updatedAt' },
  { title: 'Role', key: 'role' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'end' },
];

const confirmDelete = (item: User) => {
  selectedUser.value = item;
  deleteDialog.value = true;
};

const deleteUser = async () => {
  if (!selectedUser.value) return;

  try {
    deleteLoading.value = true;
    await axios.delete(`/api/admin/users/${selectedUser.value.id}`);
    await fetchUsers(); // Refresh the list
    deleteDialog.value = false;
  } catch (error) {
    console.error('Error deleting user:', error);
    // You might want to add error handling/notification here
  } finally {
    deleteLoading.value = false;
  }
};

const fetchUsers = async () => {
  try {
    loading.value = true;
    const response = await axios.get('/api/admin/users');
    users.value = response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchUsers();
});
</script>

<style scoped>
.v-data-table {
  width: 100%;
}
</style>
